/**
 * AYSOMART — Wishlist, Compare & Recently Viewed (ELLA revamp)
 * localStorage-backed, no app required.
 *
 * Components:
 *   <wishlist-button handle="h"> / <compare-button handle="h">
 *       Toggle a product in the given list; reflect state via .is-active.
 *   <collection-list source="wishlist|compare|recent" section="wishlist-card">
 *       Renders saved products by fetching each card from the Section
 *       Rendering API: /products/{handle}?section_id={section}
 *   [data-wishlist-count] / [data-compare-count] — text set to list size.
 *
 * Recently viewed: window.AysoRecentlyViewed.record(handle).
 */
(function () {
  const KEYS = {
    wishlist: 'aysomart:wishlist',
    compare: 'aysomart:compare',
    recent: 'aysomart:recently-viewed',
  };
  const COMPARE_MAX = 4;
  const RECENT_MAX = 12;

  const readArr = (key) => {
    try {
      const v = JSON.parse(localStorage.getItem(key));
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  };
  const writeArr = (key, arr) => {
    try {
      localStorage.setItem(key, JSON.stringify(arr));
    } catch (e) {
      /* storage disabled — fail silently */
    }
  };

  function makeStore(name, key, max) {
    return {
      name,
      all: () => readArr(key),
      has: (h) => readArr(key).includes(h),
      toggle(h) {
        let arr = readArr(key);
        const i = arr.indexOf(h);
        if (i === -1) {
          if (max && arr.length >= max) arr.shift();
          arr.push(h);
        } else {
          arr.splice(i, 1);
        }
        writeArr(key, arr);
        document.dispatchEvent(new CustomEvent(`aysomart:${name}:change`, { detail: { handles: arr } }));
        return i === -1;
      },
      record(h) {
        if (!h) return;
        let arr = readArr(key).filter((x) => x !== h);
        arr.unshift(h);
        if (max) arr = arr.slice(0, max);
        writeArr(key, arr);
      },
    };
  }

  const stores = {
    wishlist: makeStore('wishlist', KEYS.wishlist, 0),
    compare: makeStore('compare', KEYS.compare, COMPARE_MAX),
    recent: makeStore('recent', KEYS.recent, RECENT_MAX),
  };
  window.AysoWishlist = stores.wishlist;
  window.AysoCompare = stores.compare;
  window.AysoRecentlyViewed = stores.recent;

  function refreshCounts(name) {
    const n = stores[name].all().length;
    document.querySelectorAll(`[data-${name}-count]`).forEach((el) => {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  /* -------------------- toggle button (shared) -------------------- */
  class ListToggle extends HTMLElement {
    get storeName() {
      return 'wishlist';
    }
    connectedCallback() {
      this.store = stores[this.storeName];
      this.handle = this.getAttribute('handle');
      this.button = this.querySelector('button') || this;
      this.reflect();
      this._onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.reflect(this.store.toggle(this.handle));
      };
      this.button.addEventListener('click', this._onClick);
      this._onChange = () => this.reflect();
      document.addEventListener(`aysomart:${this.storeName}:change`, this._onChange);
    }
    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._onClick);
      document.removeEventListener(`aysomart:${this.storeName}:change`, this._onChange);
    }
    reflect(state) {
      const active = state === undefined ? this.store.has(this.handle) : state;
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.classList.toggle('is-active', active);
    }
  }

  class WishlistButton extends ListToggle {
    get storeName() {
      return 'wishlist';
    }
  }
  class CompareButton extends ListToggle {
    get storeName() {
      return 'compare';
    }
  }
  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('compare-button')) customElements.define('compare-button', CompareButton);

  /* -------------------- list renderer -------------------- */
  class CollectionList extends HTMLElement {
    connectedCallback() {
      this.storeName = this.getAttribute('source') || 'wishlist';
      this.store = stores[this.storeName] || stores.wishlist;
      this.section = this.getAttribute('section') || 'wishlist-card';
      this.grid = this.querySelector('[data-list-grid]') || this;
      this.empty = this.querySelector('[data-list-empty]');
      this.render();
      this._onChange = () => this.render();
      document.addEventListener(`aysomart:${this.storeName}:change`, this._onChange);
    }
    disconnectedCallback() {
      document.removeEventListener(`aysomart:${this.storeName}:change`, this._onChange);
    }
    async render() {
      const handles = this.store.all();
      if (this.empty) this.empty.hidden = handles.length > 0;
      this.grid.innerHTML = '';
      if (!handles.length) return;

      const cards = await Promise.all(
        handles.map((h) =>
          fetch(`/products/${encodeURIComponent(h)}?section_id=${this.section}`)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '')
        )
      );
      cards.forEach((html) => {
        if (!html) return;
        const wrap = document.createElement('div');
        wrap.innerHTML = html.trim();
        const inner = wrap.querySelector('[id^="shopify-section-"]') || wrap;
        this.grid.appendChild(inner);
      });
    }
  }
  if (!customElements.get('collection-list')) customElements.define('collection-list', CollectionList);

  ['wishlist', 'compare'].forEach((name) => {
    document.addEventListener(`aysomart:${name}:change`, () => refreshCounts(name));
    document.addEventListener('DOMContentLoaded', () => refreshCounts(name));
    refreshCounts(name);
  });
})();
